// Copyright ©️ 2026 eVolve MEP, LLC

import type { WithRequiredNotNull } from '@evolvemep/api-ts';
import { Button, Divider, Flex, Stack, Text, TextInput } from '@mantine/core';

import { ModalButtons } from 'components/Mantine/Modals/ModalButtons';
import { NumberInputWrapped } from 'components/Mantine/NumberInputWrapped';
import { BendPreviewContextProvider } from 'modules/Shop/EvolveFabrication/BendCreator/BendPreview/useBendPreviewContext';
import { BendCreatorBendPreview } from 'modules/Shop/EvolveFabrication/BendCreator/FormComponents/BendCreatorBendPreview';
import { BendDimensionInputs } from 'modules/Shop/EvolveFabrication/BendCreator/FormComponents/BendDimensionInputs';
import { BendMachineInfoInputs } from 'modules/Shop/EvolveFabrication/BendCreator/FormComponents/BendMachineInfoInputs';
import {
  type BendCreatorFormValues,
  useBendCreatorFormContext,
} from 'modules/Shop/EvolveFabrication/BendCreator/FormComponents/useBendCreatorFormContext';
import { useBendCreatorValidationContext } from 'modules/Shop/EvolveFabrication/BendCreator/FormComponents/useBendCreatorValidationContext';

export type ValidatedFormData = WithRequiredNotNull<
  BendCreatorFormValues,
  'benderProfile' | 'bendType' | 'conduitTypeId' | 'conduitSizeId' | 'partName' | 'quantity' | 'materialStockLength'
>;

type BendCreatorFormProps = {
  onClose: () => void;
  isEditing?: boolean;
  onSubmit: (v: ValidatedFormData) => void;
};

export const BendCreatorForm = ({ onClose, isEditing, onSubmit: parentOnSubmit }: BendCreatorFormProps) => {
  const form = useBendCreatorFormContext();

  const { lastValidation, isValidating } = useBendCreatorValidationContext();
  const serverSideValid = lastValidation?.isSuccess && !isValidating;

  const handleSubmit = async (values: typeof form.values) => {
    if (!serverSideValid) return;
    // Validation ensures that all required fields will be non-null
    const validatedValues = values as ValidatedFormData;
    parentOnSubmit(validatedValues);
  };

  return (
    <BendPreviewContextProvider>
      <form
        noValidate
        onSubmit={form.onSubmit(handleSubmit)}
        style={{ display: 'flex', flexDirection: 'column', height: '100%' }}
      >
        <Flex p={0} style={{ overflow: 'hidden' }} flex={1}>
          <Stack style={{ overflowY: 'scroll' }}>
            <Stack p="md" maw={350} bg="gray.1" flex="1 0 auto">
              <Flex justify="space-between">
                <Text size="xl" fw={500}>
                  Define Bend
                </Text>
                <Button variant="subtle" type="reset" onClick={form.reset}>
                  Reset form
                </Button>
              </Flex>
              <BendMachineInfoInputs />
              <Divider />
              <BendDimensionInputs />
              <Divider />
              <Stack gap={4}>
                <Flex gap="xs">
                  <TextInput
                    label="Part Name"
                    disabled={isEditing}
                    required
                    flex={1}
                    {...form.getInputProps('partName')}
                  />
                  <NumberInputWrapped
                    label="Quantity"
                    disabled={isEditing}
                    clampBehavior="strict"
                    required
                    w={80}
                    min={1}
                    max={99}
                    allowDecimal={false}
                    {...form.getInputProps('quantity')}
                  />
                </Flex>
                {!isEditing && (
                  <Text c="dimmed" fz="sm">
                    Part name will be incremented when adding quantities over 1.
                  </Text>
                )}
              </Stack>
            </Stack>
          </Stack>
          <BendCreatorBendPreview />
        </Flex>
        <ModalButtons
          type="submit"
          onClose={onClose}
          confirmationText={isValidating ? 'Validating bend data' : isEditing ? 'Update bend' : 'Add to work order'}
          disabled={!form.isValid() || !serverSideValid}
          loading={isValidating}
        />
      </form>
    </BendPreviewContextProvider>
  );
};
